import React from 'react';
import { Link } from 'react-router-dom';
import { useButtonLinks } from '../hooks/useButtonLinks';
import { useTranslation } from '../hooks/useTranslation';

const Indices = () => {
  const { handleButtonClick } = useButtonLinks();
  const { t } = useTranslation();
  const indicesInstruments = [
    { symbol: 'DE40', description: 'Germany 40 Index', contractSize: '1 USD', marginRate: '1', spread: '25' },
    { symbol: 'UK100', description: 'UK 100 Index', contractSize: '1 USD', marginRate: '1', spread: '0' },
    { symbol: 'US100', description: 'US Tech 100 Index', contractSize: '1 USD', marginRate: '1', spread: '0' },
    { symbol: 'US30', description: 'US Wall Street 30 Index', contractSize: '1 USD', marginRate: '1', spread: '0' },
    { symbol: 'JPN225', description: 'Japan 225 - Cash', contractSize: '1 JPY', marginRate: '4', spread: '0' }
  ];

  return (
    <div className="indices-page">
      {/* Hero Section */}
      <section className="indices-hero">
        <div className="container">
          <div className="indices-hero-content">
            <h1 className="indices-title">{t('markets.indices.hero.title')}</h1>
            <p className="indices-subtitle">
              {t('markets.indices.hero.subtitle')}
            </p>
            <div className="hero-question">
              <h2>{t('markets.indices.hero.description')}</h2>
            </div>
            <div className="indices-features">
              <div className="feature-highlight">
                <i className="fas fa-globe"></i>
                <span>{t('markets.indices.whyTrade.benefits.0.description')}</span>
              </div>
              <div className="feature-highlight">
                <i className="fas fa-chart-line"></i>
                <span>{t('markets.indices.whyTrade.benefits.1.description')}</span>
              </div>
              <div className="feature-highlight">
                <i className="fas fa-clock"></i>
                <span>{t('markets.indices.whyTrade.benefits.2.description')}</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Indices Instruments Section */}
      <section className="indices-instruments">
        <div className="container">
          <div className="instruments-header">
            <h2 className="section-title">{t('markets.indices.instruments.title')}</h2>
            <button className="btn btn-secondary">{t('markets.indices.instruments.subtitle')}</button>
          </div>
          
          <div className="instruments-table-wrapper">
            <table className="instruments-table">
              <thead>
                <tr>
                  <th>{t('markets.indices.instruments.headers.symbol')}</th>
                  <th>{t('markets.indices.instruments.headers.description')}</th>
                  <th>Contract Size</th>
                  <th>Margin Rate</th>
                  <th>{t('markets.indices.instruments.headers.minSpread')}</th>
                </tr>
              </thead>
              <tbody>
                {indicesInstruments.map((instrument, index) => (
                  <tr key={index}>
                    <td className="symbol">{instrument.symbol}</td>
                    <td className="description">{instrument.description}</td>
                    <td>{instrument.contractSize}</td>
                    <td>{instrument.marginRate}</td>
                    <td className="spread">{instrument.spread}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          
          <p className="instruments-note">
            {t('markets.indices.instruments.note')}
          </p>
        </div>
      </section>

      {/* Why Trade Indices Section */}
      <section className="why-trade-indices">
        <div className="container">
          <h2 className="section-title">{t('markets.indices.whyTrade.title')}</h2>
          <div className="benefits-grid">
            <div className="benefit-card">
              <div className="benefit-icon">
                <i className="fas fa-globe"></i>
              </div>
              <h3>{t('markets.indices.whyTrade.benefits.0.title')}</h3>
              <p>{t('markets.indices.whyTrade.benefits.0.description')}</p>
            </div>
            <div className="benefit-card">
              <div className="benefit-icon">
                <i className="fas fa-chart-line"></i>
              </div>
              <h3>{t('markets.indices.whyTrade.benefits.1.title')}</h3>
              <p>{t('markets.indices.whyTrade.benefits.1.description')}</p>
            </div>
            <div className="benefit-card">
              <div className="benefit-icon">
                <i className="fas fa-clock"></i>
              </div>
              <h3>{t('markets.indices.whyTrade.benefits.2.title')}</h3>
              <p>{t('markets.indices.whyTrade.benefits.2.description')}</p>
            </div>
            <div className="benefit-card">
              <div className="benefit-icon">
                <i className="fas fa-briefcase"></i>
              </div>
              <h3>{t('markets.indices.whyTrade.benefits.3.title')}</h3>
              <p>{t('markets.indices.whyTrade.benefits.3.description')}</p>
            </div>
            <div className="benefit-card">
              <div className="benefit-icon">
                <i className="fas fa-chart-bar"></i>
              </div>
              <h3>{t('markets.indices.whyTrade.benefits.4.title')}</h3>
              <p>{t('markets.indices.whyTrade.benefits.4.description')}</p>
            </div>
            <div className="benefit-card">
              <div className="benefit-icon">
                <i className="fas fa-shield-alt"></i>
              </div>
              <h3>{t('markets.indices.whyTrade.benefits.5.title')}</h3>
              <p>{t('markets.indices.whyTrade.benefits.5.description')}</p>
            </div>
          </div>
          
          <div className="indices-cta">
            <h3>Discover the advantages of trading Indices!</h3>
            <button 
              className="btn btn-primary"
              onClick={(e) => handleButtonClick('openAccount', e)}
            >
              Create Account
            </button>
          </div>
        </div>
      </section>

      {/* Account Comparison Section */}
      <section className="account-comparison">
        <div className="container">
          <div className="comparison-header">
            <h2 className="section-title">Pick the Best Account to Make Smart Decisions</h2>
            <p className="section-subtitle">Discover the advantages of trading Indices!</p>
          </div>
          
          <div className="accounts-cards">
            
            {/* Pro Account */}
            <div className="account-card">
              <div className="account-header">
                <h3 className="account-name">Pro</h3>
                <div className="account-price">
                  <span className="deposit-amount">$20</span>
                  <span className="deposit-label">Starting Deposit</span>
                </div>
              </div>
              <div className="account-features">
                <div className="feature-row">
                  <span className="feature-label">Spreads from</span>
                  <span className="feature-value">1 pips</span>
                </div>
                <div className="feature-row">
                  <span className="feature-label">Commission</span>
                  <span className="feature-value">$0</span>
                </div>
                <div className="feature-row">
                  <span className="feature-label">Leverage</span>
                  <span className="feature-value">1:400</span>
                </div>
                <div className="feature-row">
                  <span className="feature-label">Platform</span>
                  <span className="feature-value">MT5</span>
                </div>
                <div className="feature-row">
                  <span className="feature-label">Islamic Accounts</span>
                  <span className="feature-value">Available</span>
                </div>
                <div className="feature-row">
                  <span className="feature-label">Order Execution</span>
                  <span className="feature-value">Market</span>
                </div>
              </div>
              <Link to="/accounts/pro" className="btn btn-primary account-btn">{t('markets.indices.accountComparison.features.findOutMore')}</Link>
            </div>

            {/* Prime Account */}
            <div className="account-card featured">
              <div className="popular-badge">Most Popular</div>
              <div className="account-header">
                <h3 className="account-name">Prime</h3>
                <div className="account-price">
                  <span className="deposit-amount">$250</span>
                  <span className="deposit-label">Starting Deposit</span>
                </div>
              </div>
              <div className="account-features">
                <div className="feature-row">
                  <span className="feature-label">Spreads from</span>
                  <span className="feature-value">0 pips</span>
                </div>
                <div className="feature-row">
                  <span className="feature-label">Commission</span>
                  <span className="feature-value">$6</span>
                </div>
                <div className="feature-row">
                  <span className="feature-label">Leverage</span>
                  <span className="feature-value">1:400</span>
                </div>
                <div className="feature-row">
                  <span className="feature-label">Platform</span>
                  <span className="feature-value">MT5</span>
                </div>
                <div className="feature-row">
                  <span className="feature-label">Islamic Accounts</span>
                  <span className="feature-value">Available</span>
                </div>
                <div className="feature-row">
                  <span className="feature-label">Order Execution</span>
                  <span className="feature-value">Market</span>
                </div>
              </div>
              <Link to="/accounts/prime" className="btn btn-primary account-btn">{t('markets.indices.accountComparison.features.findOutMore')}</Link>
            </div>

            {/* Premium Account */}
            <div className="account-card">
              <div className="account-header">
                <h3 className="account-name">Premium</h3>
                <div className="account-price">
                  <span className="deposit-amount">$3000</span>
                  <span className="deposit-label">Starting Deposit</span>
                </div>
              </div>
              <div className="account-features">
                <div className="feature-row">
                  <span className="feature-label">Spreads from</span>
                  <span className="feature-value">0 pips</span>
                </div>
                <div className="feature-row">
                  <span className="feature-label">Commission</span>
                  <span className="feature-value">$3</span>
                </div>
                <div className="feature-row">
                  <span className="feature-label">Leverage</span>
                  <span className="feature-value">1:400</span>
                </div>
                <div className="feature-row">
                  <span className="feature-label">Platform</span>
                  <span className="feature-value">MT5</span>
                </div>
                <div className="feature-row">
                  <span className="feature-label">Islamic Accounts</span>
                  <span className="feature-value">Available</span>
                </div>
                <div className="feature-row">
                  <span className="feature-label">Order Execution</span>
                  <span className="feature-value">Market</span>
                </div>
              </div>
              <Link to="/accounts/premium" className="btn btn-primary account-btn">{t('markets.indices.accountComparison.features.findOutMore')}</Link>
            </div>

          </div>
          
          <div className="demo-account">
            <h3>Not ready yet?</h3>
            <p>Try the demo account first to see what's in store for you!</p>
            <button 
              className="btn btn-secondary"
              onClick={(e) => handleButtonClick('tryDemo', e)}
            >
              Create Demo Account
            </button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Indices;